import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  DATE_QUERY_FORMAT,
  JST_TIMEZONE,
  RAW_GNSS_ANALYSIS_PADDING_HOURS,
} from './analysisConfig';

dayjs.extend(utc);
dayjs.extend(timezone);

export const jstDateTimeFormatter = new Intl.DateTimeFormat('ja-JP', {
  timeZone: JST_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export const normalizeMeasuredAt = (value?: string | null) => {
  if (!value) {
    return null;
  }
  return value
    .trim()
    .replace(/([+-]\d{2}:\d{2})Z$/, '$1')
    .replace(' ', 'T');
};

export const parseMeasuredAt = (value?: string | null) => {
  const normalized = normalizeMeasuredAt(value);
  if (!normalized) {
    return null;
  }
  const epochMs = Date.parse(normalized);
  if (!Number.isNaN(epochMs)) {
    return new Date(epochMs);
  }
  return null;
};

export const formatDateTime = (value?: string | null) => {
  const parsed = parseMeasuredAt(value);
  return parsed ? jstDateTimeFormatter.format(parsed).replace(/\//g, '/') : '-';
};

export const formatDistance = (value?: number | null) =>
  typeof value === 'number' ? `${value.toFixed(1)}m` : '-';

export const formatObservedExtentDistance = (value: number | null | undefined, bufferM: number) =>
  typeof value === 'number' ? `${Math.max(value - bufferM, 0).toFixed(1)}m` : '-';

export const formatDurationSeconds = (value?: number | null) =>
  typeof value === 'number' ? `${value.toFixed(1)}s` : '-';

export const formatSpeed = (value?: number | null) =>
  typeof value === 'number' ? `${value.toFixed(2)}km/h` : '-';

export const formatAccuracy = (value?: number | null) =>
  typeof value === 'number' ? `${value.toFixed(1)}m` : '-';

export const formatMeasuredSpeed = (value?: number | null) =>
  typeof value === 'number' ? `${value.toFixed(2)}m/s` : '-';

export const formatAltitude = (value?: number | null) =>
  typeof value === 'number' ? `${value.toFixed(1)}m` : '-';

export const buildDayRange = (selectedDay: Dayjs) => {
  const start = selectedDay.tz(JST_TIMEZONE).startOf('day');
  const end = selectedDay.tz(JST_TIMEZONE).endOf('day');
  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
  };
};

export const buildRawGnssFetchRange = (selectedDay: Dayjs) => {
  const start = selectedDay
    .tz(JST_TIMEZONE)
    .startOf('day')
    .subtract(RAW_GNSS_ANALYSIS_PADDING_HOURS, 'hour');
  const end = selectedDay
    .tz(JST_TIMEZONE)
    .endOf('day')
    .add(RAW_GNSS_ANALYSIS_PADDING_HOURS, 'hour');
  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
  };
};

export const parseSelectedDayQueryParam = (value: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = dayjs.tz(`${value}T00:00:00`, JST_TIMEZONE);
  if (!parsed.isValid() || parsed.format(DATE_QUERY_FORMAT) !== value) {
    return null;
  }
  return parsed;
};

export const formatSelectedDayQueryParam = (value: Dayjs) =>
  value.tz(JST_TIMEZONE).format(DATE_QUERY_FORMAT);

export const isWithinRange = (value: string | null | undefined, startIso: string, endIso: string) => {
  const timestamp = parseMeasuredAt(value)?.getTime();
  const start = parseMeasuredAt(startIso)?.getTime();
  const end = parseMeasuredAt(endIso)?.getTime();
  if (timestamp == null || start == null || end == null) {
    return false;
  }
  return timestamp >= start && timestamp <= end;
};
